import { PartVersionSchemaVersion, currentPartVersionSchemaVersion, getSchemaVersion, isPropertyWithLinkValueType, isPropertyWithLinkValueTypeButStringValue, } from "@buildwithflux/models";
import { asMutable, lzCompressField, lzCompressedFieldName, lzDecompressField } from "@buildwithflux/shared";
import { deepCopy, flatObjectToInterface, interfaceDataToFlatObject } from "../common";
import { schemaVersionKey } from "../common/converters/schemaVersion";
/**
 * Load an IPartVersionData from an arbitrary object.
 *
 * @param data A blob of unknown type that we assume is roughly IPartVersionData shaped.
 * @returns IPartVersionData
 * @remarks This function is not safe!  It will just blindly assume that the underlying data is correct.
 * Use only if you are sure you know what you're doing.
 */
const loadFromVersionlessData = (data) => {
    // TODO: this is dumb and dangerous but equivalent to how we do it today.
    const result = deepCopy(data);
    if (result[schemaVersionKey])
        delete result[schemaVersionKey];
    return result;
};
/**
 * Load IPartVersionData from an object that conforms to version 0 of the PartVersionSchema i.e. where we have compressed the
 * pcbLayoutNodes and extracted_element_cache.
 *
 * @param data Versioned data blob
 * @returns parsed IPartVersionData with all fields decompressed and ready to use.
 */
const loadCompressedData = (data) => {
    const compressedPcbLayoutNodes = data[lzCompressedFieldName("pcbLayoutNodes")];
    const compressedExtractedElementCache = data[lzCompressedFieldName("extracted_element_cache")];
    const result = {
        ...data,
        pcbLayoutNodes: {},
        extracted_element_cache: {},
    };
    if (compressedPcbLayoutNodes)
        lzDecompressField(result, "pcbLayoutNodes");
    if (compressedExtractedElementCache)
        lzDecompressField(result, "extracted_element_cache");
    // TODO: Convert numeric terminalId to string for pad nodes to fix type inconsistency
    // where terminalId is sometimes stored as number in Firestore but schema expects string.
    // See: https://linear.app/buildwithflux/issue/FLUX-6812
    if (result.pcbLayoutNodes) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(result.pcbLayoutNodes).forEach((node) => {
            if (typeof node === "object" && node !== null && "type" in node && "terminalId" in node) {
                if (node.type === "pad" && typeof node.terminalId === "number") {
                    node.terminalId = String(node.terminalId);
                }
            }
        });
    }
    delete result[schemaVersionKey];
    return result;
};
/**
 * Load part version data from an object by delegating to functions that can read data with a given schema.
 *
 * @param data Data of unknown type or schema version.
 * @param _propertyConverter use this to deserialize property, handle different schema there
 * @param firestoreDataId The Firestore document ID of the data
 * @returns An IPartVersionData object
 * @throws Error if the schema version is not recognized.
 *
 * NOTE: firestoreDataId passing here is no longer supported - need to find a way to do this
 */
export function loadPartVersionData(data, firestoreDataId) {
    // Suppress warning for non-flat objects since data from Algolia/Firestore may legitimately contain arrays/maps/sets
    const unFlattenedData = flatObjectToInterface(data, { warnOnNonFlatObject: false });
    const schemaVersion = getSchemaVersion(unFlattenedData);
    let partVersionData;
    switch (schemaVersion) {
        case undefined:
            partVersionData = loadFromVersionlessData(unFlattenedData);
            break;
        case PartVersionSchemaVersion.compressedElementsAndNodes:
            partVersionData = loadCompressedData(unFlattenedData);
            break;
        case PartVersionSchemaVersion.linkPropValue:
            partVersionData = loadCompressedData(unFlattenedData);
            const properties = partVersionData.properties;
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.keys(properties).forEach((key) => {
                const property = properties[key];
                /**
                 * TODO: This is step one of migrating property schema in part version data: by support reading
                 * the new schema from our storage layer.
                 *
                 * Later when we change how frontend process the new schema, we will come back here and change how we deserialize it:
                 * at that point, when deserialize, we need to reverse this operation by mapping an old schema to the new one
                 */
                if (isPropertyWithLinkValueType(property)) {
                    properties[key].value = property.value.text;
                }
            });
            partVersionData.properties = properties;
            break;
        // TODO: need to clean this up
        default:
            throw new Error("unrecognized version");
    }
    // version does not always exist in Firestore!!!
    if (!partVersionData.version && firestoreDataId) {
        asMutable(partVersionData).version = firestoreDataId;
    }
    return partVersionData;
}
/**
 * Convert an IPartVersionData object into an object with the latest schema version attached.
 */
const dumpPartVersionData = (data) => {
    const result = {
        ...data,
        [schemaVersionKey]: currentPartVersionSchemaVersion,
    };
    if (result.pcbLayoutNodes)
        lzCompressField(result, "pcbLayoutNodes");
    if (result.extracted_element_cache)
        lzCompressField(result, "extracted_element_cache");
    const properties = result.properties;
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.keys(properties).forEach((key) => {
        const property = properties[key];
        /**
         * TODO: This is step one of migrating property schema in part version data: by support writing the new schema to
         * our storage layer
         *
         * Later when we change how client processes the new schema, we will come back here and change how we serialize it:
         * at that point, we don't need to handle serialization at all as long as deserialization is handled correctly.
         */
        if (isPropertyWithLinkValueTypeButStringValue(property)) {
            if (properties[key]) {
                properties[key].value = {
                    type: "link",
                    text: property.value,
                    url: property.value,
                };
            }
        }
    });
    result.properties = properties;
    return interfaceDataToFlatObject(result);
};
export class PartVersionAdminConverter {
    toFirestore(modelObject) {
        return dumpPartVersionData(modelObject);
    }
    fromFirestore(snapshot) {
        return loadPartVersionData(snapshot.data(), snapshot.id);
    }
}
export class PartVersionClientConverter {
    toFirestore(modelObject) {
        return dumpPartVersionData(modelObject);
    }
    fromFirestore(snapshot) {
        return loadPartVersionData(snapshot.data(), snapshot.id);
    }
}
//# sourceMappingURL=PartVersionConverter.js.map